<script setup>
  import { formatDuration } from '@/utils/date'
  import { handleError } from '@/utils/error'
  import { getEmployerUrl } from '@/utils/url'
  import { askLogin } from '@/utils/ask-login'

  const { href } = useRequestURL()
  const config = useRuntimeConfig()
  const { fullPath, hash, params, path, query } = useRoute()
  const client = useStrapiClient()
  const { user } = useUser()
  const dayjs = useDayjs()

  const applyModal = ref(null)
  const openApplyModal = () => applyModal.value.open()
  const hasApplied = ref(false)

  const jobFetcher = reactive({
    ...(await useAsyncData(`job-${params.slug}`, async () => {
      const options = {
        params: {
          fields: [
            'advantages',
            'applicationEmail',
            'answer1',
            'answer2',
            'answer3',
            'crushes',
            'canLetterQuestions',
            'clspContacts',
            'durationCount',
            'durationUnit',
            'enhanceWithAI',
            'expiresAt',
            'externalUrl',
            'imported',
            'mandatoryLetter',
            'salaryMin',
            'salaryMax',
            'salaryPeriod',
            'salaryType',
            'slug',
            'status',
            'teleworkable',
            'title',
            'type',
            'jobSheetVisible'
          ],
          filters: { published: true },
          populate: {
            employer: {
              fields: ['defaultCrushes', 'description', 'name', 'slug', 'visible'],
              populate: {
                careerSite: { fields: ['active', 'slug'] },
                cover: { fields: ['url'] }
              }
            },
            header: {
              fields: ['title'],
              populate: {
                cover: { fields: ['url'] }
              }
            },
            jobSheet: { fields: ['url'] },
            locations: { fields: ['city', 'street', 'postalCode', 'name'] },
            seo: {
              fields: ['metaDescription', 'metaTitle', 'metaRobots', 'structuredData'],
              populate: {
                metaImage: { fields: ['url'] }
              }
            },
            speaker: {
              fields: ['firstName', 'function', 'lastName', 'linkedin', 'title'],
              populate: {
                photo: { fields: ['url'] }
              }
            }
          }
        }
      }
      if (query.preview?.length) options.query = { preview: query.preview }
      const res = await client(`/jobs/${params.slug}`, options)
      return res.data
    }))
  })

  if (jobFetcher.error) {
    throw createError({ statusCode: 404, statusMessage: 'Job not found', url: path, fatal: true })
  }

  const job = computed(() => {
    const j = jobFetcher.data
    if (!j) return null
    return {
      ...j,
      isExpired() {
        return j.expiresAt < dayjs().format('YYYY-MM-DD')
      },
      isExternal() {
        return !!j.externalUrl
      },
      isStatus(status) {
        return status === j.status
      }
    }
  })

  let j
  if (job.value) {
    j = job.value
    hasApplied.value = user.value?.hasApplied(j.id)

    const referrer = useReferrer()
    if (process.client) {
      const originUrl = sessionStorage.getItem(`job:${j.id}`)
      if (!originUrl && referrer.value) sessionStorage.setItem(`job:${j.id}`, referrer.value)
      referrer.value = null
    }

    if (process.client && !query.preview) {
      client(`/jobs/${j.id}/view`, {
        body: { url: href },
        method: 'POST'
      }).catch(error => handleError(error))
    }

    const seo = j.seo ?? {}
    useSeo({
      canonicalUrl: config.public.baseUrl + fullPath.split('?')[0],
      metaTitle: seo.metaTitle ?? `${j.title} - ${j.employer?.name}`,
      metaDescription: seo.metaDescription ?? j.answer2,
      metaRobots: seo.metaRobots,
      structuredData: !j.isExpired() ? seo.structuredData : null
    })

    useHead({
      meta: [{ hid: 'og:image', property: 'og:image', content: useStrapiMedia(seo.metaImage?.url ?? j.employer?.cover?.url) }]
    })
  }

  function redirect() {
    client(`/jobs/${job.value.id}/redirect`, { method: 'PUT' }).catch(error => handleError(error))
  }
  console.log('job', job.value)
</script>

<template>
  <Loading class="mx-auto max-w-screen-xl px-4 py-16" v-if="jobFetcher.pending" />
  <template v-else-if="job">
    <ModalApply
      :job="job"
      :mandatoryLetter="job.mandatoryLetter"
      :canLetterQuestions="job.canLetterQuestions"
      :otherFileField="true"
      ref="applyModal"
      @applied="hasApplied = true"
    />
    <div
      class="relative h-60 overflow-x-hidden bg-cover bg-center sm:h-80"
      :style="{
        backgroundImage: `url(${useStrapiMedia(job.header?.cover ? job.header.cover.url : job.employer?.cover?.url)})`
      }"
    >
      <div class="h-full bg-black/50">
        <div class="mx-auto flex h-full max-w-screen-xl flex-col items-center justify-center space-y-4 px-4 text-center">
          <h1 class="font-special text-2xl font-bold text-white sm:text-3xl" v-text="job.title" v-if="job.title" />
          <div class="font-bold text-viking-400 sm:text-xl" v-if="job.header || job.employer">
            {{ job.header?.title ? job.header.title : job.employer?.name }}
          </div>
        </div>
      </div>
    </div>

    <div class="sticky top-16 z-10 bg-tarawera text-white">
      <div class="mx-auto max-w-screen-xl p-4 text-center">
        <div class="-mx-4 -my-2 flex flex-wrap items-center justify-evenly sm:grid-cols-2 xl:grid-cols-4">
          <div class="mx-4 my-2 space-y-1">
            <div class="font-special text-sm font-bold uppercase opacity-50">Type de poste</div>
            <div>
              <template v-if="job.type === 'public'">
                <span>Emploi public</span>
                <Tooltip content="Le secteur public est ouvert à tous, avec ou sans concours">
                  <Icon class="ml-2 text-viking-400" name="circle-info" />
                </Tooltip>
              </template>
              <template v-else>{{ convertJobType(job.type) }}</template>
              <span class="text-sm" v-if="job.durationCount && !['cdi', 'public', 'mandate'].includes(job.type)">
                ({{ formatDuration(job.durationCount, job.durationUnit) }})</span
              >
            </div>
          </div>

          <div class="mx-4 my-2 space-y-1" v-if="job.locations?.length">
            <div class="font-special text-sm font-bold uppercase opacity-50">Basé à</div>
            <div v-if="job.locations[0].city">
              <Tooltip :content="job.locations.map(p => [p.street, p.postalCode, p.city].filter(Boolean).join(', ')).join(' / ')" placement="bottom">
                <span class="underline">
                  {{ job.locations[0].city + (job.locations.length > 1 ? ', ' + job.locations[1].city.substring(0, 3) + '...' : '') }}
                </span>
              </Tooltip>
            </div>
            <div v-else>{{ job.locations[0].name }}</div>
          </div>

          <div class="mx-4 my-2 space-y-1">
            <div class="font-special text-sm font-bold uppercase opacity-50">Rémunération envisagée</div>
            <div v-if="job.salaryMin === null && (job.salaryMax === 0 || job.salaryMax === null)">Selon profil</div>
            <div v-else-if="job.salaryMin !== null && job.salaryMax === null">
              À partir de {{ job.salaryMin }}€ {{ job.salaryType ? job.salaryType : 'brut' }} par {{ job.salaryPeriod === 'month' ? 'mois' : 'an' }}
            </div>
            <div v-else-if="job.salaryMin === job.salaryMax">
              {{ job.salaryMin }}€ {{ job.salaryType ? job.salaryType : 'brut' }} par {{ job.salaryPeriod === 'month' ? 'mois' : 'an' }}
            </div>
            <div v-else>
              Entre {{ job.salaryMin }}€ et {{ job.salaryMax }}€ {{ job.salaryType ? job.salaryType : 'brut' }} par
              {{ job.salaryPeriod === 'month' ? 'mois' : 'an' }}
            </div>
          </div>

          <div class="mx-4 my-2 space-y-1" v-if="job.teleworkable">
            <div class="font-special text-sm font-bold uppercase opacity-50">Télétravail</div>
            <div>Possible</div>
          </div>

          <div class="mx-4 my-2" v-if="!job.isExpired()">
            <a v-if="job.imported" :href="`mailto:${job.applicationEmail}?subject=${job.title}`">
              <Button type="button">Je postule par email</Button>
            </a>
            <NuxtLink v-else-if="job.isExternal()" target="_blank" :to="job.externalUrl">
              <Button type="button" @click="redirect">Je postule</Button>
            </NuxtLink>
            <template v-else-if="job.isStatus('active')">
              <Button v-if="!user" type="button" @click="askLogin(fullPath.replace(hash, '') + '#postuler')">Je postule</Button>
              <Button v-else-if="user.candidate && !hasApplied" type="button" @click="openApplyModal">Je postule</Button>
              <Tooltip v-else placement="top">
                <Button class="pointer-events-none opacity-50">Je postule</Button>
                <template v-if="!user.candidate" #content><span class="text-tarawera-900">Vous n’avez pas d’espace candidat</span></template>
                <template v-else #content><span class="text-tarawera-900">Vous avez déjà postulé à cette offre</span></template>
              </Tooltip>
            </template>
          </div>
        </div>
      </div>
    </div>

    <div class="w- mx-auto max-w-screen-lg px-4 pb-16 pt-6">
      <div class="mb-4 flex items-center justify-end space-x-2 text-right">
        <span class="text-zinc-500">Partager</span>
        <NuxtLink
          class="duration-300 hover:text-facebook"
          target="_blank"
          :to="`https://www.facebook.com/sharer/sharer.php?u=${config.public.baseUrl}${fullPath}`"
        >
          <Icon fixedWidth name="facebook-f" size="lg" type="brands" />
        </NuxtLink>
        <NuxtLink
          class="duration-300 hover:text-twitter"
          target="_blank"
          :to="`https://twitter.com/intent/tweet?url=${config.public.baseUrl}${fullPath}`"
        >
          <Icon fixedWidth name="twitter" size="lg" type="brands" />
        </NuxtLink>
        <NuxtLink
          class="duration-300 hover:text-linkedin"
          target="_blank"
          :to="`https://www.linkedin.com/sharing/share-offsite/?url=${config.public.baseUrl}${fullPath}`"
        >
          <Icon fixedWidth name="linkedin-in" size="lg" type="brands" />
        </NuxtLink>
      </div>

      <div class="flex flex-col items-center justify-center space-y-2 sm:flex-row sm:space-x-4 sm:space-y-0" v-if="job.speaker">
        <Profile
          :profile="{
            linkedin: job.speaker.linkedin,
            photo: job.speaker.photo?.url ? useStrapiMedia(job.speaker.photo.url) : undefined
          }"
        />
        <div class="text-center text-lg font-bold text-zinc-500 sm:text-left">
          Cette offre est présentée par<br />{{
            `${job.speaker.firstName} ${job.speaker.lastName}${job.speaker.function ? `, ${job.speaker.function}` : ''}`
          }}
        </div>
      </div>

      <div class="mt-16 space-y-4" v-if="job.answer2">
        <template v-if="job.enhanceWithAI">
          <div class="font-special text-lg font-bold md:text-xl">🎯 Le défi à relever&nbsp;?</div>
          <div class="leading-[1.4]" v-html="job.answer2.replace(/\n/g, '<br />')" v-if="job.answer2" />
        </template>
        <template v-else>
          <div class="font-special text-lg font-bold md:text-xl">Présentation de l’organisation</div>
          <Quote>
            <Markdown v-if="job.answer2" :content="job.answer2" />
          </Quote>
        </template>
      </div>

      <div class="mt-8 space-y-4" v-if="job.answer1">
        <div class="font-special text-lg font-bold md:text-xl">
          <template v-if="job.enhanceWithAI">🔎 Les missions qui attendent les candidats en quelques mots&nbsp;?</template>
          <template v-else>Description des missions</template>
        </div>
        <Markdown v-if="job.answer1" :content="job.answer1" />
      </div>

      <div class="mt-8 space-y-4" v-if="job.employer?.visible">
        <div class="font-special text-lg font-bold md:text-xl">
          <template v-if="job.enhanceWithAI">👩‍💻 L’environnement de travail&nbsp;?</template>
          <template v-else>L’environnement de travail</template>
        </div>
        <div>
          <template v-if="job.enhanceWithAI"
            >Parcourez les
            <NuxtLink class="text-viking-400 underline duration-300 hover:text-viking-500" :to="getEmployerUrl(job.employer)"
              >coulisses de notre organisation</NuxtLink
            >
            pour en savoir plus sur notre équipe et nos projets innovants.</template
          >
          <template v-else
            >👉 Découvrez les
            <NuxtLink class="text-viking-400 underline duration-300 hover:text-viking-500" :to="getEmployerUrl(job.employer)"
              >coulisses de notre organisation</NuxtLink
            ></template
          >
        </div>
      </div>

      <div class="mt-8 flex flex-col justify-between space-x-0 space-y-8 md:flex-row md:space-x-8 md:space-y-0">
        <div class="flex-1 space-y-4">
          <div class="font-special text-lg font-bold md:text-xl">
            <template v-if="job.enhanceWithAI">☝️ Ce poste est fait pour vous si&nbsp;:</template>
            <template v-else>Profil recherché</template>
          </div>
          <!-- <ul class="list-disc ml-4 space-y-2 marker:text-viking-400" v-if="job.answer3">
            <li :key="index" v-for="(item, index) in job.answer3.split('\n')" v-text="item" />
          </ul> -->
          <Markdown v-if="job.answer3" :content="job.answer3" />
        </div>

        <div class="flex-1 self-center" v-if="job.enhanceWithAI">
          <div class="relative mt-2 rounded-md bg-zinc-100 p-4 sm:mr-4 sm:px-6 md:px-4 lg:px-6" v-if="job.crushes || job.employer?.defaultCrushes">
            <NuxtImg class="absolute -top-4 right-2 h-16 w-16 rounded-full sm:-right-4" src="/images/logo-pp-400x400.webp" alt="Logo Profil Public" />
            <div class="font-special text-lg font-bold">Les petits +</div>
            <div class="text-zinc-500">dénichés par Profil Public</div>
            <ul class="mt-4 space-y-2">
              <li v-for="(item, index) in (job.crushes || job.employer?.defaultCrushes).split('\n')" v-text="item" :key="index" />
            </ul>
          </div>
        </div>
      </div>

      <div class="mt-8 space-y-4" v-if="job.advantages">
        <div class="font-special text-lg font-bold md:text-xl">Avantages</div>
        <Markdown :content="job.advantages" />
      </div>

      <div class="mt-8 space-y-4" v-if="job.clspContacts">
        <div class="font-special text-lg font-bold md:text-xl">Personnes à contacter</div>
        <ul>
          <li
            class="prose list-inside list-disc whitespace-nowrap marker:text-viking-400"
            v-for="(email, index) in job.clspContacts.split('\n')"
            :key="index"
          >
            {{ email }}
          </li>
        </ul>
      </div>

      <div
        class="mt-8 font-special text-lg font-bold md:text-xl"
        v-if="job.jobSheet && (job.jobSheetVisible || job.jobSheetVisible === undefined || job.jobSheetVisible === null)"
      >
        👉 Téléchargez
        <NuxtLink class="text-viking-400 underline duration-300 hover:text-viking-500" target="_blank" :to="useStrapiMedia(job.jobSheet.url)"
          >l'offre d'emploi</NuxtLink
        >
      </div>

      <div class="mt-8 text-center">
        <div class="text-lg text-red-500" v-if="!job.isStatus('active')">L’offre est actuellement hors ligne.</div>
        <div class="text-lg text-red-500" v-else-if="job.isExpired()">La date limite de candidature est dépassée.</div>
        <template v-else>
          <a v-if="job.imported" :href="`mailto:${job.applicationEmail}?subject=${job.title}`">
            <Button size="lg" type="button">Je postule par email</Button>
          </a>
          <NuxtLink v-else-if="job.isExternal()" target="_blank" :to="job.externalUrl">
            <Button size="lg" type="button" @click="redirect">Je postule</Button>
          </NuxtLink>
          <template v-else-if="job.isStatus('active')">
            <Button v-if="!user" size="lg" type="button" @click="askLogin(fullPath.replace(hash, '') + '#postuler')">Je postule</Button>
            <Button v-else-if="user.candidate && !hasApplied" size="lg" type="button" @click="openApplyModal">Je postule</Button>
            <Tooltip v-else placement="top">
              <Button class="pointer-events-none opacity-50" size="lg">Je postule</Button>
              <template v-if="!user.candidate" #content>Vous n’avez pas d’espace candidat</template>
              <template v-else #content>Vous avez déjà postulé à cette offre</template>
            </Tooltip>
          </template>
          <div class="mt-4 text-zinc-500" v-if="job.expiresAt">
            Date limite de candidature&nbsp;:
            {{ new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(job.expiresAt)) }}
          </div>
        </template>
      </div>
    </div>
  </template>
</template>
